import React, { useEffect, useMemo, useRef, useState } from 'react';
import axiosInstance from '../axiosConfig';
import { Link, useLocation } from 'react-router-dom';

// Modal removed for Address & Salary UI (kept logic/state intact)

function UpdateEmployee() {
  const location = useLocation();
  const activePath = location.pathname;

  const [currentUser, setCurrentUser] = useState(null);
  const [message, setMessage] = useState('');

  const selectedDocumentId = localStorage.getItem('selectedDocumentId');

  const [formDocumentData, setFormDocumentData] = useState({
    employee_code: '',
    contact_number: '',
    email: '',
    first_name: '',
    middle_name: '',
    last_name: '',
    father_first_name: '',
    father_middle_name: '',
    father_last_name: '',
    gender: '',
    date_of_birth: '',
    department: '',
    position: '',
    address: '',
    date_of_joining: '',
    offer_date: '',
  });

  // Inline Address (modal-based to match Documents.js)
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [pincode, setPincode] = useState('');

  const [showUpdateAddressModal, setShowUpdateAddressModal] = useState(false);

  // Keep for logic compatibility; UI is inline now.


  const updateAddressObjectFromString = (addressStr) => {
    // AddEmployee stores: "{line1}, {line2}, {state} - {pincode}"
    const safeStr = addressStr || '';

    const [left, right] = safeStr.split('-').map((s) => s.trim());
    const pincodePart = right || '';
    const leftParts = left ? left.split(',').map((s) => s.trim()) : [];

    setAddressLine1(leftParts[0] || '');
    setAddressLine2(leftParts[1] || '');
    setSelectedState(leftParts[2] || '');
    setPincode(pincodePart);
  };

  const selectedBranchFromLS = localStorage.getItem('selectedBranch');
  const selectedBranchIdFromLS = Number(localStorage.getItem('selectedBranchId'));


  // Salary: modal-based to match Documents.js (and to keep DP World shared salary)
  const [epfEnabled, setEpfEnabled] = useState(true);
  const [esicEnabled, setEsicEnabled] = useState(true);

  // Keep employer and employee toggles independent (UI shows separate rows)
  const [employerEpfEnabled, setEmployerEpfEnabled] = useState(true);
  const [employerEsicEnabled, setEmployerEsicEnabled] = useState(true);

  const [showUpdateSalaryModal, setShowUpdateSalaryModal] = useState(false);

  const [updateSalary, setUpdateSalary] = useState({
    basic: 0,
    da: 0,
    hra: 0,
    bonus: 0,
    special_allowance: 0,
    other1: 0,
    other2: 0,
  });

  const safe = (v) => (v === '' || v === null || v === undefined ? 0 : Number(v));

  // DP World Panipat shared salary helpers (same constants + behavior as Documents.js)
  const SHARED_SALARY_KEY = 'dp_world_panipat_shared_salary';


  const isDPWorldPanipatBranch = (branchName) => branchName === 'DP World Panipat';

  const getSharedSalary = () => {
    try {
      const saved = localStorage.getItem(SHARED_SALARY_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  };

  const saveSharedSalary = (salaryData) => {
    try {
      localStorage.setItem(SHARED_SALARY_KEY, JSON.stringify(salaryData));
    } catch (error) {
      console.error('Failed to save shared salary:', error);
    }
  };

  const preloadSalaryUpdate = (data = {}) => {
    // UpdateEmployee must always reflect the current API response.
    // LocalStorage shared salary is only used as a fallback when API values are missing.

    const apiBasic = Number(data.basic);
    const apiDa = Number(data.da);
    const apiHra = Number(data.hra);
    const apiBonus = Number(data.bonus);
    const apiSpecial = Number(data.special_allowance);
    const apiOther1 = Number(data.other1);
    const apiOther2 = Number(data.other2);

    const hasAnyApiValue =
      !Number.isNaN(apiBasic) ||
      !Number.isNaN(apiDa) ||
      !Number.isNaN(apiHra) ||
      !Number.isNaN(apiBonus) ||
      !Number.isNaN(apiSpecial) ||
      !Number.isNaN(apiOther1) ||
      !Number.isNaN(apiOther2);

    if (isDPWorldPanipatBranch(selectedBranchFromLS) && !hasAnyApiValue) {
      const sharedSalary = getSharedSalary();
      if (sharedSalary) {
        setUpdateSalary({
          basic: Number(sharedSalary.basic) || 0,
          da: Number(sharedSalary.da) || 0,
          hra: Number(sharedSalary.hra) || 0,
          bonus: Number(sharedSalary.bonus) || 0,
          special_allowance: Number(sharedSalary.special_allowance) || 0,
          other1: Number(sharedSalary.other1) || 0,
          other2: Number(sharedSalary.other2) || 0,
        });
        return;
      }
    }

    setUpdateSalary({
      basic: Number(data.basic) || 0,
      da: Number(data.da) || 0,
      hra: Number(data.hra) || 0,
      bonus: Number(data.bonus) || 0,
      special_allowance: Number(data.special_allowance) || 0,
      other1: Number(data.other1) || 0,
      other2: Number(data.other2) || 0,
    });
  };

  const handleUpdateSalaryChange = (e) => {
    const { name, value } = e.target;
    setUpdateSalary((prev) => ({
      ...prev,
      [name]: Number(value) || 0,
    }));
  };

  const {
    updateGrossTotal,
    updateEmployeeEPF,
    updateEmployerEPF,
    updateEmployeeESIC,
    updateEmployerESIC,
    updateTakeHome,
    updateCTC,
  } = useMemo(() => {
    const basic = Number(updateSalary.basic) || 0;
    const da = Number(updateSalary.da) || 0;
    const hra = Number(updateSalary.hra) || 0;
    const bonus = Number(updateSalary.bonus) || 0;
    const special = Number(updateSalary.special_allowance) || 0;
    const other1 = Number(updateSalary.other1) || 0; // deduction
    const other2 = Number(updateSalary.other2) || 0; // earning

    const gross = basic + da + hra + bonus + special + other2;

    const cappedBasic = Math.min(basic, 15000);
    const empEPF = epfEnabled ? Math.round(cappedBasic * 0.12) : 0;
    const emplEPF = employerEpfEnabled ? Math.round(cappedBasic * 0.13) : 0;

    const empESIC = esicEnabled && gross <= 21000 ? Math.round(gross * 0.0075) : 0;
    const emplESIC = employerEsicEnabled && gross <= 21000 ? Math.round(gross * 0.0325) : 0;

    const takeHome = gross - other1 - empEPF - empESIC;
    const ctc = gross + emplEPF + emplESIC;

    return {
      updateGrossTotal: gross,
      updateEmployeeEPF: empEPF,
      updateEmployerEPF: emplEPF,
      updateEmployeeESIC: empESIC,
      updateEmployerESIC: emplESIC,
      updateTakeHome: takeHome,
      updateCTC: ctc,
    };
  }, [updateSalary, epfEnabled, esicEnabled]);

  const fetchAllDocumentsByBranchName = async (branchName) => {
    // Uses existing /documents/ list API shape from Documents.js
    // We page until we collect all IDs.
    const storedBranchId = Number(localStorage.getItem('selectedBranchId'));

    let page = 1;
    const rowsPerPage = 50; // bigger page size to reduce requests
    let all = [];

    while (true) {
      const res = await axiosInstance.get('/documents/', {
        params: {
          page,
          limit: rowsPerPage,
          search: '',
          department: '',
          branch_id: storedBranchId,
          status: 'enabled',
        },
      });

      const docs = res?.data?.data || [];
      all = all.concat(docs);

      const totalPages = res?.data?.total_pages || 1;
      if (page >= totalPages) break;
      page += 1;
    }

    // Safety: filter by exact branch name if backend returns multiple branches for branch_id.
    return all.filter((d) => d?.branch === branchName || d?.branch_name === branchName);
  };

  const saveUpdatedSalary = async () => {
    const salaryToSave = {
      ...updateSalary,
      gross_total: updateGrossTotal,
      employee_epf: epfEnabled ? updateEmployeeEPF : 0,
      employee_esic: esicEnabled ? updateEmployeeESIC : 0,
      employer_epf: epfEnabled ? updateEmployerEPF : 0,
      employer_esic: esicEnabled ? updateEmployerESIC : 0,
      take_home: updateTakeHome,
      ctc: updateCTC,
      epf_enabled: epfEnabled,
      esic_enabled: esicEnabled,
    };

    setFormDocumentData((prev) => ({
      ...prev,
      ...salaryToSave,
    }));

    if (isDPWorldPanipatBranch(selectedBranchFromLS)) {
      // Update ALL DP World Panipat documents on backend
      // (so appointment/hindi letter uses correct salary)
      try {
        const docs = await fetchAllDocumentsByBranchName('DP World Panipat');
        await Promise.all(
          docs.map((d) => axiosInstance.patch(`/documents/${d.id}/`, salaryToSave))
        );
      } catch (e) {
        // keep UI stable but show error
        console.error('Failed to mass update DP World Panipat salary:', e);
        setMessage('Failed to mass update DP World Panipat salary. Please try again.');
        return;
      }

      saveSharedSalary(salaryToSave);
    }

    setShowUpdateSalaryModal(false);
  };

  const handleDocumentChange = (event) => {
    const { name, value } = event.target;
    setFormDocumentData((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    const userId = localStorage.getItem('id');
    if (!userId) return;

    axiosInstance
      .get(`/user/${userId}/`)
      .then((res) => setCurrentUser(res.data))
      .catch(() => setCurrentUser(null));
  }, []);

  const normalizeToYMD = (value) => {
    if (!value) return '';
    if (typeof value !== 'string') {
      // number/timestamp => try Date conversion
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return '';
      return d.toISOString().slice(0, 10);
    }

    const v = value.trim();

    // Already YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;

    // ISO datetime: 2024-01-31T00:00:00Z => take date part
    const iso = v.match(/^(\d{4}-\d{2}-\d{2})/);
    if (iso) return iso[1];

    // DD/MM/YYYY => convert
    const dm = v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (dm) return `${dm[3]}-${dm[2]}-${dm[1]}`;

    // DD-MM-YYYY => convert
    const dh = v.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (dh) return `${dh[3]}-${dh[2]}-${dh[1]}`;

    // Fallback: Date parse
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
  };

  useEffect(() => {
    if (!selectedDocumentId) return;

    axiosInstance
      .get(`/documents/${selectedDocumentId}/`)
      .then((res) => {
        const doc = res.data;

    setFormDocumentData({
          employee_code: doc.employee_code || '',
          contact_number: doc.contact_number || '',
          email: doc.email || '',
          first_name: doc.first_name || '',
          middle_name: doc.middle_name || '',
          last_name: doc.last_name || '',
          father_first_name: doc.father_first_name || '',
          father_middle_name: doc.father_middle_name || '',
          father_last_name: doc.father_last_name || '',
          gender: doc.gender || '',
          date_of_birth: normalizeToYMD(doc.date_of_birth),
          department: doc.department || '',
          position: doc.position || '',
          address: doc.address || '',
          date_of_joining: normalizeToYMD(doc.date_of_joining),
          offer_date: normalizeToYMD(doc.offer_date),

          // salary values (for full formDocumentData PATCH)
          basic: doc.basic || 0,
          da: doc.da || 0,
          hra: doc.hra || 0,
          bonus: doc.bonus || 0,
          special_allowance: doc.special_allowance || 0,
          other1: doc.other1 || 0,
          other2: doc.other2 || 0,
          gross_total: doc.gross_total || 0,
          employee_epf: doc.employee_epf || 0,
          employee_esic: doc.employee_esic || 0,
          take_home: doc.take_home || 0,
          employer_epf: doc.employer_epf || 0,
          employer_esic: doc.employer_esic || 0,
          ctc: doc.ctc || 0,
          epf_enabled: doc.epf_enabled ?? true,
          esic_enabled: doc.esic_enabled ?? true,
        });

        // 🔥 Keep salary table inputs editable + in sync with this API response.
        // If DP World Panipat, preload shared cache; otherwise use this document values.
        if (isDPWorldPanipatBranch(doc.branch || selectedBranchFromLS)) {
          preloadSalaryUpdate(doc);
        } else {
          setUpdateSalary({
            basic: Number(doc.basic) || 0,
            da: Number(doc.da) || 0,
            hra: Number(doc.hra) || 0,
            bonus: Number(doc.bonus) || 0,
            special_allowance: Number(doc.special_allowance) || 0,
            other1: Number(doc.other1) || 0,
            other2: Number(doc.other2) || 0,
          });
        }

    setEpfEnabled(doc.epf_enabled ?? true);
        setEsicEnabled(doc.esic_enabled ?? true);

        // Employer toggles follow the same backend flags for now.
        // (Backend currently only provides epf_enabled/esic_enabled.)
        setEmployerEpfEnabled(doc.epf_enabled ?? true);
        setEmployerEsicEnabled(doc.esic_enabled ?? true);

        updateAddressObjectFromString(doc.address || '');
      })
      .catch((e) => {
        console.error(e);
        setMessage('Failed to load employee.');
      });
  }, [selectedDocumentId]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage('');

    try {
      await axiosInstance.patch(`/documents/${selectedDocumentId}/`, formDocumentData);
      setMessage('Document updated successfully!');
    } catch (error) {
      if (error?.response?.data) {
        const errorMessages = Object.entries(error.response.data)
          .map(([key, value]) => (Array.isArray(value) ? value.join(', ') : value))
          .join(', ');
        setMessage(errorMessages);
      } else {
        setMessage('An error occurred. Please try again.');
      }
    }
  };

  const states = useMemo(
    () => [
      'Andhra Pradesh',
      'Arunachal Pradesh',
      'Assam',
      'Bihar',
      'Chhattisgarh',
      'Delhi',
      'Goa',
      'Gujarat',
      'Haryana',
      'Himachal Pradesh',
      'Jharkhand',
      'Karnataka',
      'Kerala',
      'Madhya Pradesh',
      'Maharashtra',
      'Manipur',
      'Meghalaya',
      'Mizoram',
      'Nagaland',
      'Odisha',
      'Punjab',
      'Rajasthan',
      'Sikkim',
      'Tamil Nadu',
      'Telangana',
      'Tripura',
      'Uttar Pradesh',
      'Uttarakhand',
      'West Bengal',
      'Jammu & Kashmir',
      'Ladakh',
    ],
    []
  );

  const selectedBranch = localStorage.getItem('selectedBranch');

  return (
    <div className="dashboard-wrapper">
      <div className="dashboard-sidebar">
        <div className="sidebar-header">
          <img src="/logo.png" alt="Logo" className="sidebar-logo" />
        </div>

        <nav className="sidebar-nav">
          <Link
            to="/admin-dashboard"
            className={`sidebar-link ${activePath === '/admin-dashboard' ? 'active' : ''}`}
          >
            <i className="fas fa-home"></i> Dashboard
          </Link>

          {currentUser?.role_name === 'admin' && (
            <Link to="/users" className={`sidebar-link ${activePath === '/users' ? 'active' : ''}`}>
              <i className="fas fa-users"></i> Users
            </Link>
          )}

          <Link
            to="/departments"
            className={`sidebar-link ${activePath === '/departments' ? 'active' : ''}`}
          >
            <i className="fas fa-file-alt"></i> Departments
          </Link>
        </nav>
      </div>

      <div
        className="dashboard-main"
        style={{
          display: 'flex',
          gap: '20px',
          flexWrap: 'nowrap',
          alignItems: 'flex-start',
          padding: '20px',
        }}
      >
        <div className="dashboard-content" style={{ flex: 3 }}>
          <div className="add-employee-page" style={{ maxHeight: 'calc(100vh - 60px)', overflowY: 'auto' }}>
            <form onSubmit={handleSubmit}>
              <div className="doc">
                <h2>Update Employee</h2>
              </div>
              <hr />

              <div className="form-row1">
                <div className="form-group">
                  <label className="user-label">Employee Code:</label>
                  <input
                    type="text"
                    name="employee_code"
                    value={formDocumentData.employee_code}
                    onChange={handleDocumentChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="user-label">Contact Number*</label>
                  <input
                    type="text"
                    name="contact_number"
                    value={formDocumentData.contact_number}
                    onChange={handleDocumentChange}
                    maxLength="10"
                    inputMode="numeric"
                    onKeyPress={(e) => !/[0-9]/.test(e.key) && e.preventDefault()}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Email:</label>
                  <input type="email" name="email" value={formDocumentData.email} onChange={handleDocumentChange} />
                </div>
              </div>

              <div className="form-row1">
                <div className="form-group">
                  <label>First Name:</label>
                  <input
                    type="text"
                    name="first_name"
                    value={formDocumentData.first_name}
                    onChange={handleDocumentChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Middle Name:</label>
                  <input type="text" name="middle_name" value={formDocumentData.middle_name} onChange={handleDocumentChange} />
                </div>

                <div className="form-group">
                  <label>Last Name:</label>
                  <input type="text" name="last_name" value={formDocumentData.last_name} onChange={handleDocumentChange} />
                </div>
              </div>

              <div className="form-row1">
                <div className="form-group">
                  <label>Father's First Name:</label>
                  <input
                    type="text"
                    name="father_first_name"
                    value={formDocumentData.father_first_name}
                    onChange={handleDocumentChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Father's Middle Name:</label>
                  <input type="text" name="father_middle_name" value={formDocumentData.father_middle_name} onChange={handleDocumentChange} />
                </div>

                <div className="form-group">
                  <label>Father's Last Name:</label>
                  <input type="text" name="father_last_name" value={formDocumentData.father_last_name} onChange={handleDocumentChange} />
                </div>
              </div>

              <div className="form-row1">
                <div className="form-group">
                  <label>Date Of Birth</label>
                  <input type="date" name="date_of_birth" value={formDocumentData.date_of_birth} onChange={handleDocumentChange} required />
                </div>

                <div className="form-group">
                  <label>Gender:</label>
                  <div className="select">
                    <select name="gender" value={formDocumentData.gender} onChange={handleDocumentChange} required>
                      <option value="">Select Gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Date Of Joining</label>
                  <input type="date" name="date_of_joining" value={formDocumentData.date_of_joining} onChange={handleDocumentChange} />
                </div>

                <div className="form-group">
                  <label>Offer Date</label>
                  <input type="date" name="offer_date" value={formDocumentData.offer_date} onChange={handleDocumentChange} />
                </div>

                <div className="form-group">
                  <label className="user-label">Position</label>
                  <input type="text" name="position" value={formDocumentData.position} onChange={handleDocumentChange} />
                </div>

                <div className="form-group">
                  <label>Department:</label>
                  <input type="text" name="department" value={formDocumentData.department} disabled />
                </div>
              </div>

              <div className="form-row1">
                <div className="form-group">
                  <label>Branch:</label>
                  <input type="text" name="branch" value={selectedBranch || ''} disabled />
                </div>
              </div>

              <hr style={{ border: 'none', borderTop: '1px solid #ddd' }} />


              {/* ADDRESS (inline like AddEmployee) */}
              <div style={{ width: '100%', marginTop: 16 }}>

                <div className="form-group" style={{ marginTop: 12 }}>
                  <label className="user-label">Address</label>

                  <div className="form-row1" style={{ marginTop: 8 }}>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>Address Line 1</label>
                      <input
                        type="text"
                        value={addressLine1}
                        onChange={(e) => setAddressLine1(e.target.value)}
                      />
                    </div>

                    <div className="form-group" style={{ flex: 1 }}>
                      <label>Address Line 2</label>
                      <input
                        type="text"
                        value={addressLine2}
                        onChange={(e) => setAddressLine2(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="form-row1">
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>Pincode*</label>
                      <input
                        type="text"
                        value={pincode}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d{0,6}$/.test(value)) setPincode(value);
                        }}
                        maxLength="6"
                        
                      />
                    </div>

                    <div className="form-group" style={{ flex: 1 }}>
                      <label>State*</label>
                      <select
                        value={selectedState}
                        onChange={(e) => setSelectedState(e.target.value)}
                        
                      >
                        <option value="">Select State</option>
                        {states.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="submit-all-btn"
                    onClick={() => {
                      const fullAddress = `${addressLine1}, ${addressLine2 ? addressLine2 + ', ' : ''}${selectedState} - ${pincode}`;
                      setFormDocumentData((prev) => ({ ...prev, address: fullAddress }));
                    }}
                    style={{ marginTop: 8 }}
                  >
                    Save Address
                  </button>

                  {formDocumentData.address && (
                    <p style={{ fontSize: '12px', color: 'green', marginTop: 6 }}>
                      Address Updated ✔
                    </p>
                  )}
                </div>
              </div>

              {/* SALARY (inline like AddEmployee) */}
              <div className="form-group" style={{ marginTop: 16 }}>
                <hr style={{ border: 'none', borderTop: '1px solid #ddd' }} />
                <label className="user-label">Salary</label>

                <table className="salary-table" style={{ marginTop: 8 }}>
                  <thead>
                    <tr>
                      <th>Particulars</th>
                      <th>Monthly (₹)</th>
                      <th>Annual (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><strong>BASIC</strong></td>
                      <td>
                        <input
                          type="number"
                          name="basic"
                          value={updateSalary.basic}
                          onChange={handleUpdateSalaryChange}
                        />
                      </td>
                      <td>{safe(updateSalary.basic) * 12}</td>
                    </tr>

                    <tr>
                      <td><strong>DA</strong></td>
                      <td>
                        <input type="number" name="da" value={updateSalary.da} onChange={handleUpdateSalaryChange} />
                      </td>
                      <td>{safe(updateSalary.da) * 12}</td>
                    </tr>

                    <tr>
                      <td><strong>HRA</strong></td>
                      <td>
                        <input type="number" name="hra" value={updateSalary.hra} onChange={handleUpdateSalaryChange} />
                      </td>
                      <td>{safe(updateSalary.hra) * 12}</td>
                    </tr>

                    <tr>
                      <td><strong>Bonus</strong></td>
                      <td>
                        <input type="number" name="bonus" value={updateSalary.bonus} onChange={handleUpdateSalaryChange} />
                      </td>
                      <td>{safe(updateSalary.bonus) * 12}</td>
                    </tr>

                    <tr>
                      <td><strong>Special Allowance</strong></td>
                      <td>
                        <input
                          type="number"
                          name="special_allowance"
                          value={updateSalary.special_allowance}
                          onChange={handleUpdateSalaryChange}
                        />
                      </td>
                      <td>{safe(updateSalary.special_allowance) * 12}</td>
                    </tr>

                    <tr>
                      <td><strong>Gross Total</strong></td>
                      <td><strong>{updateGrossTotal}</strong></td>
                      <td><strong>{updateGrossTotal * 12}</strong></td>
                    </tr>

                    <tr>
                      <td>
                        Employee EPF (12%)
                        <span
                          onClick={() => setEpfEnabled(!epfEnabled)}
                          style={{ cursor: 'pointer', marginLeft: 10 }}
                        >
                          {epfEnabled ? '✔' : '✖'}
                        </span>
                      </td>
                      <td>{epfEnabled ? updateEmployeeEPF : 0}</td>
                      <td>{epfEnabled ? updateEmployeeEPF * 12 : 0}</td>
                    </tr>

                    <tr>
                      <td>
                        Employee ESIC (0.75%)
                        <span
                          onClick={() => setEsicEnabled(!esicEnabled)}
                          style={{ cursor: 'pointer', marginLeft: 10 }}
                        >
                          {esicEnabled ? '✔' : '✖'}
                        </span>
                      </td>
                      <td>{esicEnabled ? updateEmployeeESIC : 0}</td>
                      <td>{esicEnabled ? updateEmployeeESIC * 12 : 0}</td>
                    </tr>

                    <tr>
                      <td><strong>Other 1</strong></td>
                      <td>
                        <input type="number" name="other1" value={updateSalary.other1} onChange={handleUpdateSalaryChange} />
                      </td>
                      <td>{safe(updateSalary.other1) * 12}</td>
                    </tr>

                    <tr>
                      <td><strong>Take Home</strong></td>
                      <td><strong>{updateTakeHome}</strong></td>
                      <td><strong>{updateTakeHome * 12}</strong></td>
                    </tr>

                    <tr>
                      <td>
                        Employer EPF (13%)
                        <span
                          onClick={() => setEmployerEpfEnabled(!employerEpfEnabled)}
                          style={{ cursor: 'pointer', marginLeft: 10 }}
                        >
                          {employerEpfEnabled ? '✔' : '✖'}
                        </span>
                      </td>
                      <td>{employerEpfEnabled ? updateEmployerEPF : 0}</td>
                      <td>{employerEpfEnabled ? updateEmployerEPF * 12 : 0}</td>
                    </tr>

                    <tr>
                      <td>
                        Employer ESIC (3.25%)
                        <span
                          onClick={() => setEmployerEsicEnabled(!employerEsicEnabled)}
                          style={{ cursor: 'pointer', marginLeft: 10 }}
                        >
                          {employerEsicEnabled ? '✔' : '✖'}
                        </span>
                      </td>
                      <td>{employerEsicEnabled ? updateEmployerESIC : 0}</td>
                      <td>{employerEsicEnabled ? updateEmployerESIC * 12 : 0}</td>
                    </tr>

                    <tr>
                      <td><strong>Other 2</strong></td>
                      <td>
                        <input type="number" name="other2" value={updateSalary.other2} onChange={handleUpdateSalaryChange} />
                      </td>
                      <td>{safe(updateSalary.other2) * 12}</td>
                    </tr>

                    <tr>
                      <td><strong>CTC</strong></td>
                      <td><strong>{updateCTC}</strong></td>
                      <td><strong>{updateCTC * 12}</strong></td>
                    </tr>
                  </tbody>
                </table>

                <button
                  className="submit-all-btn"
                  type="button"
                onClick={async () => {
                    // SaveUpdatedSalary now also performs mass-update for DP World Panipat
                    await saveUpdatedSalary();
                    setMessage('Document updated successfully!');
                  }}
                  style={{ marginTop: 8 }}
                >
                  Save Salary
                </button>

                {Number(formDocumentData.basic) > 0 && (
                  <p style={{ fontSize: '12px', color: 'green', marginTop: 6 }}>
                    Salary Updated ✔
                  </p>
                )}
              </div>

              <hr />
              <button className="submit-all-btn" type="submit">
                Update
              </button>

              {message && <p style={{ color: 'red' }}>{message}</p>}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UpdateEmployee;





